import { DataPair, newDataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData';
import { getVisibleFoldersInLayers, history, RETAIN_PARAMS_PARAM } from './Router';

export function decodeLegacyPageName(pageName?: string | null) {
  return pageName
    ? pageName
        // take old escaped spaces, since new tool encodes them as '+'
        .replace(/%20/g, ' ')
        // old book-marked URLs will have these numeric encodings for these. Can't imagine why
        .replace(/\|01\|/g, '/')
        .replace(/\|02\|/g, '.')
        .replace(/\|03\|/g, '&')
        .replace(/\|04\|/g, '%')
        .replace(/\|05\|/g, '<')
        .replace(/\|06\|/g, '>')
        .replace(/\|07\|/g, '#')
        .replace(/\|08\|/g, '+')
    : '';
}

// we should not need to re-encode for legacy

// TODO Should these be with router components, to separate these navigation concerns from the Portal itself? e.g. embedded forms?

/**
 * @param location current location object
 * @returns parts from query params, and maybe in future, path parts
 */
export function getLocationPartsFromUrl(location = history.location) {
  const params = new URLSearchParams(location.search);
  const folderId = params.get('FolderId') || undefined;
  const pageName = params.get('pageName') || undefined;
  // if folder and page ID are the same, onFolderNavigation will be a NOOP
  return { folderId, pageName: decodeLegacyPageName(pageName) };
}

export function getNavigationParams(search = history.location.search, preFiltered = false) {
  // legacy filtered out /FolderId/i, /pageName/i, /pageId/i, /FlowId/i, /retainparameters/i, in a loop.
  // we have a regex to do that in one go:
  const additionalParams = preFiltered ? search : getAdditionalParameters(search);
  // in future, make sense to pass around a URLSearchParams, instead of POJO?
  // could use their iterator that way, instead of Object.keys, or JSON.stringify, etc.
  const params = new URLSearchParams(additionalParams);
  const object: { [key: string]: string } = {};
  const dataPairs: DataPair[] = [];

  params.forEach((value, key) => {
    object[key] = value;
    dataPairs.push(newDataPair({ Name: key, OutputValue: value }));
  });

  return { object, dataPairs };
}

/**
 * Strip out standard "reserved word" query parameters.
 * @param search query string to filter
 * @returns query string, minus any standard nav parameters.
 */
export function getAdditionalParameters(search = history.location.search) {
  return (
    search
      .replace(/^\?/, '') // strip preceding ?s
      // replace any of these, including their values, but not if they are the value of some additional parameter
      // using a positive lookahead for & or the end of the string
      .replace(/((^|&)(FolderId|pageName|pageId|FlowId|retainparameters)=.*?)(?=&|$)/gi, '')
      .replace(/&$/, '') // strip trailing &'s
  );
}

// $DP.Navigation.Helper.UpdateNavigationParameters
// and it's siblings need to be handled as well, or we'll end up with differing parameter formats... probably...
// it calls, and conditionally updateUrlFromNavigationParams
// updateUrlFromNavigationParams - updates history, based on new params object, but preserving FolderId and PageName
// splitting this off into [DT-035640]

export function getBasePath(baseUrl: string, isStudio?: boolean) {
  baseUrl = baseUrl || portalBaseURL;
  // in some situations, like nav from entity details we won't have isStudio flag to check
  isStudio = isStudio ?? baseUrl.toLocaleLowerCase().includes('studio');
  // path is everything in the portal base URL past the host
  let baseUrlPath = baseUrl.split(window.location.host)[1];

  if (!baseUrlPath) {
    return '';
  }

  // hard to see where it all used to come from. All I found was in _layout.cshtml, which was essentially:
  // window.location.origin + window.location.pathname + "H/" + window.location.search;
  // Meaning Studio getting turned into StudioH
  // It seems kind of arbitrary, and of course no doc comments as to why these particular paths are still important :/

  const suffix = isStudio ? '/StudioH/' : '/H/'; // why do we need the H path? I bet we don't actually need it.
  // replace any mangled bits of old/odd paths, case insensitive:
  return baseUrlPath.replace(/\/(Studio(H)*)|\/(H(UI)*(?!\w))/i, '').replace(/\/$/, '') + suffix;
}

/**
 * A helper for handling retain-parameters in navigationParameters object, since that needs to be handled a
 * little differently than retain-parameters as a URL param. Both have same result, but the object shouldn't result in the URL param.
 * @param folderId incoming
 * @param navigationParameters object being passed
 * @param retainParameters defaults to `true` if FolderID is changing. Pass `false` to preserve params, regardless of presence of retain parameters param
 * @returns object
 */
export function evaluateNavParams(
  folderId: string,
  navigationParameters: { [key: string]: string | boolean },
  retainParameters = false
) {
  const { folderId: currentFolderId } = getLocationPartsFromUrl();

  retainParameters = retainParameters || folderId === currentFolderId;
  for (let key of Object.keys(navigationParameters)) {
    if (retainParameters) {
      break;
    }
    let retainParam = (retainParameters = key.toLowerCase() === RETAIN_PARAMS_PARAM);
    if (retainParam) {
      // we don't need to also push this into the URL, if it's being handled as an object.
      delete navigationParameters[key];
    }
  }
  return retainParameters ? navigationParameters : {};
}

/** This is used to imperatively check which folders are currently visible to update, etc. */
export function getVisibleFolders() {
  // there might be something about "DecisionsFramework.ServiceLayer.Services.Folder.AliasFolderBehavior" I missed...
  // but I did not find a way to reproduce it.
  return [
    // current folder will always be true, it's only the others that are conditional
    getLocationPartsFromUrl().folderId as string,
    // not adding a pretty selector for visibleFolders, because by the time we render these things in React,
    // we really should handle the update events & related checks in a different way. Prove me wrong.
    ...getVisibleFoldersInLayers(),
  ];
}
